import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Check, X, RefreshCw, ExternalLink, Loader2, Share2 } from "lucide-react";
import SocialsEditor from "@/components/trust/SocialsEditor";
import HoldersStrip from "@/components/trust/HoldersStrip";

const LEVEL = {
  verified: { color: "#00ff41", label: "SolSpur Verified", Icon: ShieldCheck },
  building: { color: "#cfff04", label: "Trust building", Icon: ShieldAlert },
  unverified: { color: "#ff3333", label: "Unverified — do your own research", Icon: ShieldAlert },
};

const LINKS = [["solscan", "Solscan"], ["rugcheck", "RugCheck"], ["dexscreener", "DexScreener"], ["x", "X"], ["telegram", "Telegram"], ["website", "Website"]];

/** Public buyer-facing trust checklist for a coin page. */
export default function TrustPanel({ coin, onChanged }) {
  const [report, setReport] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => {
    api.get(`/coins/${coin.slug}/trust`).then((r) => setReport(r.data)).catch(() => {});
  }, [coin.slug]);

  useEffect(() => { load(); }, [load]);

  const recheck = async () => {
    setChecking(true);
    try {
      const r = await api.post(`/coins/${coin.slug}/rug-check`);
      toast.success(r.data.rug_safe ? "On-chain check passed" : "Checked — authorities not fully revoked");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't re-verify");
    } finally {
      setChecking(false);
    }
  };

  if (!report) return <div className="border border-[#3a3a3a] bg-[#121212] h-40 animate-pulse mb-6" data-testid="trust-loading" />;

  const lv = LEVEL[report.level] || LEVEL.unverified;
  const links = LINKS.filter(([k]) => report.links?.[k]);

  return (
    <section className="border bg-[#121212] mb-6" style={{ borderColor: lv.color }} data-testid="trust-panel">
      <div className="p-5 border-b border-[#3a3a3a] flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <lv.Icon size={22} style={{ color: lv.color }} />
          <div>
            <div className="font-display font-black uppercase tracking-tight text-lg" style={{ color: lv.color }} data-testid="trust-level">
              {lv.label}
            </div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]" data-testid="trust-score">
              Trust score {report.score}/100 · {report.passed}/{report.total} checks
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {report.verified_post && (
            <a
              href={`https://x.com/intent/tweet?text=${encodeURIComponent(report.verified_post)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="trust-verified-post"
              className="btn-brutal bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-[10px] px-3 py-2 inline-flex items-center gap-1"
              onClick={() => api.post("/me/share-ping", { slug: coin.slug }).catch(() => {})}
            >
              <Share2 size={10} /> Post "Verified" on X
            </a>
          )}
          <div className="w-32 h-2 bg-[#0c0c0c] border border-[#3a3a3a]">
            <div className="h-full transition-all duration-700" style={{ width: `${report.score}%`, background: lv.color }} />
          </div>
          {coin.mint_address && (
            <button
              onClick={recheck}
              disabled={checking}
              data-testid="trust-recheck"
              className="font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04] inline-flex items-center gap-1"
            >
              {checking ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Re-verify on-chain
            </button>
          )}
        </div>
      </div>

      <ul className="divide-y divide-[#2a2a2a]" data-testid="trust-checks">
        {report.checks.map((c) => (
          <li key={c.id} className="px-5 py-3 flex items-start gap-3" data-testid={`trust-check-${c.id}`} data-ok={c.ok ? "1" : "0"}>
            <span className={`mt-0.5 w-4 h-4 flex items-center justify-center border ${c.ok ? "border-[#00ff41] text-[#00ff41]" : "border-[#333] text-[#b8b8b8]"}`}>
              {c.ok ? <Check size={10} strokeWidth={3} /> : <X size={10} />}
            </span>
            <div className="min-w-0">
              <div className={`font-mono text-xs ${c.ok ? "text-white" : "text-[#d0d0d0]"}`}>{c.label}</div>
              <div className="text-[11px] text-[#b8b8b8]">{c.detail}</div>
            </div>
          </li>
        ))}
      </ul>

      <HoldersStrip slug={coin.slug} />

      <div className="px-5 py-3 border-t border-[#3a3a3a] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {links.map(([k, label]) => (
            <a
              key={k}
              href={report.links[k]}
              target="_blank"
              rel="noreferrer"
              data-testid={`trust-link-${k}`}
              className="font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04] inline-flex items-center gap-1"
            >
              {label} <ExternalLink size={9} />
            </a>
          ))}
          {report.checked_at && (
            <span className="font-mono text-[11px] text-[#b8b8b8]" data-testid="trust-checked-at">
              on-chain checked {new Date(report.checked_at).toLocaleDateString()}
            </span>
          )}
        </div>
        {coin.is_owner && <SocialsEditor coin={coin} onSaved={() => { load(); onChanged?.(); }} />}
      </div>
    </section>
  );
}
